<script setup lang="ts">
import TheWelcome from '../components/TheWelcome.vue'
import { onMounted } from 'vue'

onMounted(() => {
  // Initialize animations
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('animate-fadeInUp')
      }
    })
  }, { threshold: 0.1 })

  document.querySelectorAll('.animate-on-scroll').forEach(el => {
    observer.observe(el)
  })
})
</script>

<template>
  <main class="overflow-hidden">
    <!-- Hero Section -->
    <section class="relative pt-32 pb-20 md:pt-40 md:pb-28">
      <div class="absolute inset-0 overflow-hidden">
        <div class="absolute inset-0 bg-gradient-to-br from-green-100/30 to-cyan-100/30"></div>
        <div class="absolute inset-0 bg-[url('@/assets/grid-pattern.svg')] opacity-10"></div>

        <!-- Floating elements -->
        <div class="absolute top-1/4 left-1/4 w-40 h-40 bg-green-400/20 rounded-full filter blur-3xl opacity-70"></div>
        <div class="absolute bottom-1/3 right-1/4 w-60 h-60 bg-cyan-400/20 rounded-full filter blur-3xl opacity-70"></div>
      </div>

      <div class="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div class="text-center animate-on-scroll opacity-0 translate-y-10 transition-all duration-700">
          <h1 class="text-6xl md:text-8xl font-black text-gray-900 mb-6 tracking-tighter">
            <span class="bg-clip-text text-transparent bg-gradient-to-r from-green-700 via-emerald-600 to-cyan-700">
              Future of Farm Operations
            </span>
          </h1>
          <p class="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto font-medium leading-relaxed">
            Rochex Farm Management is a high-performance ecosystem designed to scale agriculture with precision data and elite operational control.
          </p>
          <div class="mt-12 flex flex-col sm:flex-row justify-center gap-6">
            <RouterLink
              to="/register"
              class="px-10 py-4 bg-gray-900 text-white rounded-2xl font-black shadow-2xl hover:bg-black transition-all hover:-translate-y-1 active:scale-95"
            >
              Get Started Free
            </RouterLink>
            <RouterLink
              to="/about"
              class="px-10 py-4 bg-white text-gray-900 border border-gray-200 rounded-2xl font-black shadow-lg hover:bg-gray-50 transition-all hover:-translate-y-1 active:scale-95"
            >
              Our Mission
            </RouterLink>
          </div>
        </div>

        <!-- Hero Illustration -->
        <div class="mt-16 animate-on-scroll opacity-0 translate-y-10 transition-all duration-700 delay-150">
          <div class="glass-card bg-white/70 rounded-3xl p-6 shadow-2xl max-w-4xl mx-auto hover-3d">
            <div class="aspect-w-16 aspect-h-9 bg-gradient-to-br from-green-100 to-cyan-100 rounded-2xl overflow-hidden">
              <img
                src="@/assets/farm-dashboard-preview.png"
                alt="Farm Management Dashboard"
                class="w-full h-full object-cover float-animation"
              >
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Features Section -->
    <section class="py-20 bg-gradient-to-b from-white to-green-50">
      <div class="max-w-7xl mx-auto px-6 lg:px-8">
        <div class="text-center mb-16 animate-on-scroll opacity-0 translate-y-10 transition-all duration-700">
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900">
            <span class="bg-clip-text text-transparent bg-gradient-to-r from-green-600 to-cyan-600">
              Powerful Farming Features
            </span>
          </h2>
          <p class="text-lg text-gray-600 max-w-2xl mx-auto mt-4">
            Everything you need to manage your farm efficiently in one platform
          </p>
        </div>

        <TheWelcome />
      </div>
    </section>
  </main>
</template>

<style>
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fadeInUp {
  animation: fadeInUp 0.8s ease-out forwards;
}
</style>
